import { formatDate } from '@artosera/shared';
import type { EtatConsultation } from '../../horsLigne/index.js';
import { onlineManager, useQueryClient } from '@tanstack/react-query';
import { WifiOff } from 'lucide-react';
import type { JSX } from 'react';
import { useCallback, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './BandeauHorsLigne.module.css';

/** L'état réseau vu par TanStack Query (celui qui met les requêtes en pause). */
export function useEnLigne(): boolean {
  return useSyncExternalStore(
    (rappel) => onlineManager.subscribe(rappel),
    () => onlineManager.isOnline(),
    () => true,
  );
}

/**
 * Une requête attend-elle le réseau sans avoir jamais rien reçu ? C'est une
 * page jamais chargée, ouverte hors ligne : elle chargerait sans fin.
 */
function usePageNonChargee(): boolean {
  const client = useQueryClient();
  const lire = useCallback(
    () =>
      client
        .getQueryCache()
        .findAll()
        .some(
          (requete) => requete.state.fetchStatus === 'paused' && requete.state.data === undefined,
        ),
    [client],
  );
  return useSyncExternalStore(
    useCallback((rappel) => client.getQueryCache().subscribe(rappel), [client]),
    lire,
    () => false,
  );
}

/**
 * Bandeau de la coquille hors ligne (relecture du 23/09, constats 6 et 7) :
 * sans réseau, seules les pages déjà chargées sont disponibles, et les droits
 * affichés n'ont plus été revérifiés ; une page jamais chargée le dit au lieu
 * de charger sans fin ; un changement de galerie refusé aussi.
 */
export function BandeauHorsLigne({
  galerieRefusee,
  consultation,
}: {
  readonly galerieRefusee: boolean;
  readonly consultation?: EtatConsultation | null;
}): JSX.Element | null {
  const { t, i18n } = useTranslation('regie');
  const enLigne = useEnLigne();
  const pageNonChargee = usePageNonChargee();
  if (enLigne && !consultation) return null;
  return (
    <div className={styles.bandeau} role="status">
      <span className={styles.icone} aria-hidden="true">
        <WifiOff size={16} strokeWidth={2} />
      </span>
      <span className={styles.texte}>
        {consultation
          ? t('horsLigne.consultation', {
              date: formatDate(
                new Date(consultation.enregistreLe),
                i18n.language === 'en' ? 'en' : 'fr',
                'court',
              ),
              expire: formatDate(
                new Date(consultation.expireLe),
                i18n.language === 'en' ? 'en' : 'fr',
                'court',
              ),
            })
          : t('horsLigne.pagesChargees')}
        {galerieRefusee ? ` ${t('horsLigne.galerieRefusee')}` : ''}
        {pageNonChargee ? ` ${t('horsLigne.pageNonChargee')}` : ''}
      </span>
    </div>
  );
}
